/**
 * @file migrations.js
 * @location src/utils/migrations.js
 * @role localStorage 스키마 마이그레이션 본체 (saveToStorage / loadFromStorage 는 storage.js)
 *
 * 저장 키 `workdo_v2` 의 `_version` 필드 기반 스키마 버전 관리.
 *
 * ── 베이스라인 (v1) ──
 * 프론트엔드 완성 시점에 과거 v0~v59 누적 마이그 이력(검수 루프·일보·부서·직급·ToDo 등 폐기 history)을
 * 청산하고 **현재 작동 스키마를 v1 단일 베이스라인**으로 리셋했다(백엔드 통합 전 깨끗한 출발점).
 * 백엔드 없는 pre-1.0 단계라 호환해야 할 실 데이터가 없음. 과거 이력은 git·docs/razor-archive.md 로만 보존.
 *   - `_version < BASELINE_VERSION`(없음/v0) 데이터 → 업그레이드 경로 없음 → migrate 가 null 반환
 *     → loadFromStorage 가 그대로 null 반환 → getInitialState 가 기본 시드 + 온보딩으로 부팅(초기화)
 *   - `_version >= BASELINE_VERSION` 데이터 → forward 루프 (현재 v1 은 무변경 통과)
 *
 * ⚠️ 미래 스키마 변경 시:
 *   1. `STEPS` 에 `2: migrateV2` 형태로 forward 스텝 추가
 *   2. 본 파일 `CURRENT_VERSION` 과 storage.js `SCHEMA_VERSION` 을 함께 올림
 * loadFromStorage 는 `_version < SCHEMA_VERSION` 일 때만 migrate 호출.
 *
 * 회귀 가드: tests/migrations.test.js (베이스라인 통과 · 구버전 초기화).
 */

/** 베이스라인 — 이 버전 미만 데이터는 업그레이드하지 않고 초기화한다. */
export const BASELINE_VERSION = 1;

/** 현재 스키마 버전. 미래 스텝 추가 시 storage.js SCHEMA_VERSION 과 함께 올림. */
export const CURRENT_VERSION = 1;

/** forward 마이그 스텝 (미래 v2+ 만 추가). 키 = 도달 버전, 값 = (data) => data. */
const STEPS = {};

/**
 * 구버전 데이터를 현재 스키마로 마이그레이션합니다.
 * 베이스라인 미만(업그레이드 경로 없음)이면 null 을 반환 — 호출자가 초기화로 처리.
 * @returns {object|null}
 */
export function migrate(data) {
  let version = data._version ?? 0;
  if (version < BASELINE_VERSION) return null;

  let d = { ...data };
  while (version < CURRENT_VERSION) {
    const next = version + 1;
    const stepFn = STEPS[next];
    if (!stepFn) break;
    d = stepFn(d);
    d._version = next;
    version = next;
  }
  return d;
}
